import {
  authUserFrom,
  can,
  normalizeLogin,
  type AuthUser,
  type Permission,
  type Role,
} from '@pms/shared';
import { create } from 'zustand';

import { NetworkError } from '../sync/api.js';
import * as api from './api.js';
import { rememberCredential, verifyOffline } from './credentials.js';
import {
  clearSession,
  clearToken,
  forgetPersonalData,
  loadSession,
  saveLocalUser,
  saveSession,
} from './session.js';

/**
 * Вход в систему.
 *
 * Вход один для всех — логин и пароль. Если сети нет, пароль сверяется с
 * хэшом, который устройство посчитало само при прошлом успешном входе:
 * смену закрывают в цеху, где связи может не быть неделю.
 *
 * Токен при этом не появляется — его выдаёт сервер. Пока его нет, отчёты
 * копятся в очереди и уйдут, когда связь вернётся. Данные не теряются.
 */

export type AuthStatus =
  | 'loading' // читаем сохранённую сессию
  | 'anonymous' // никто не вошёл
  | 'authorized' // вошли, токен есть
  | 'offline-authorized' // вошли без сети, токена ещё нет
  | 'token-expired'; // работали, но сессия кончилась — нужен повторный вход

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
  busy: boolean;

  restore(): Promise<void>;
  login(login: string, password: string): Promise<boolean>;
  logout(): Promise<void>;
  markTokenExpired(): Promise<void>;
  /** Позвать человека войти заново, не теряя, кто работает. */
  requireReauth(): void;
  clearError(): void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,
  error: null,
  busy: false,

  async restore() {
    const stored = await loadSession();
    if (!stored.user) {
      set({ status: 'anonymous', user: null });
      return;
    }
    const expired = stored.expiresAt !== null && Date.parse(stored.expiresAt) <= Date.now();
    if (!stored.token || expired) {
      set({ status: stored.token ? 'token-expired' : 'offline-authorized', user: stored.user });
      return;
    }
    set({ status: 'authorized', user: stored.user });
  },

  async login(login, password) {
    set({ busy: true, error: null });
    // Тем же приведением, что и на сервере: иначе запись в кэше устройства
    // легла бы под одним написанием, а искалась под другим
    const trimmed = normalizeLogin(login);
    try {
      const session = await api.loginWithPassword({ login: trimmed, password });
      // На устройство зашёл другой человек — чужие уведомления убираем,
      // иначе он увидит их как свои, а своих не получит вовсе
      const previous = get().user;
      if (previous && previous.id !== session.user.id) await forgetPersonalData();
      await saveSession(session);
      // Запоминаем на устройстве, чтобы следующий вход работал и без сети
      await rememberCredential(trimmed, password, session.user);
      set({ status: 'authorized', user: session.user, busy: false });
      return true;
    } catch (error) {
      if (!(error instanceof NetworkError)) {
        set({ busy: false, error: error instanceof Error ? error.message : 'Не удалось войти' });
        return false;
      }

      const cached = await verifyOffline(trimmed, password);
      if (cached) {
        const user = authUserFrom({
          id: cached.userId,
          fullName: cached.fullName,
          role: cached.role as Role,
        });
        await saveLocalUser(user);
        set({ status: 'offline-authorized', user, busy: false });
        return true;
      }

      set({
        busy: false,
        error: 'Нет связи. Без сети войти может только тот, кто уже входил на этом устройстве.',
      });
      return false;
    }
  },

  /**
   * Выход. Очередь неотправленного при этом остаётся: она принадлежит смене,
   * а не учётной записи. Следующий человек на общем планшете вводит свои
   * логин и пароль, а накопленное уйдёт от имени того, кто его создал.
   *
   * А вот уведомления уходят вместе с человеком: они личные, и следующему
   * на этом планшете видеть их незачем.
   */
  async logout() {
    try {
      if (get().status === 'authorized') await api.logoutOnServer();
    } catch {
      // Связи может не быть — локальную сессию всё равно закрываем
    }
    await clearSession();
    await forgetPersonalData();
    set({ status: 'anonymous', user: null, error: null });
  },

  async markTokenExpired() {
    await clearToken();
    set({ status: 'token-expired' });
  },

  requireReauth() {
    set({ status: 'token-expired', error: null });
  },

  clearError() {
    set({ error: null });
  },
}));

/** Проверка права у текущего пользователя. В интерфейсе — для скрытия кнопок. */
export function useCan(permission: Permission): boolean {
  const user = useAuthStore((state) => state.user);
  return user ? can(user.role, permission) : false;
}
